/**
 * Onboard an agency directly, with no registration behind it.
 *
 * There is deliberately **no credential field**, and now nothing to send
 * either: the agency signs in with a code texted to the mobile number typed
 * here. That number is therefore the whole of the account — get it wrong and
 * the person cannot get in, which is why the confirmation reads it back.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowSquareOut, Buildings, CheckCircle, Info, User } from '@phosphor-icons/react';
import { useOnboardAgency, type OnboardVars } from '../queries/platform';
import { apiError } from '../lib/api';
import type { Agency } from '../lib/types';
import { Field, SubmitButton } from '../components/ui';

type Errors = Partial<Record<keyof OnboardVars, string>>;

const empty: OnboardVars = {
  agencyName: '',
  contactName: '',
  phone: '',
  email: '',
  address: '',
  website: '',
};

export default function Onboard() {
  const [form, setForm] = useState<OnboardVars>(empty);
  const [errors, setErrors] = useState<Errors>({});
  const [created, setCreated] = useState<{
    agency: Agency;
    message: string;
  } | null>(null);

  const onboard = useOnboardAgency();

  const set = (key: keyof OnboardVars, value: string) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => (e[key] ? { ...e, [key]: undefined } : e));
  };

  const validate = (): boolean => {
    const next: Errors = {};
    if (!form.agencyName.trim()) next.agencyName = 'Agency name is required';
    if (!form.contactName.trim()) next.contactName = 'Contact name is required';
    if (!/^\d{10}$/.test(form.phone)) next.phone = 'Enter a valid 10-digit mobile number';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = 'Enter a valid email address';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      const response = await onboard.mutateAsync({
        agencyName: form.agencyName.trim(),
        contactName: form.contactName.trim(),
        phone: form.phone,
        email: form.email.trim(),
        address: form.address?.trim() || undefined,
        website: form.website?.trim() || undefined,
      });
      setCreated({
        agency: response.data,
        message: response.message,
      });
    } catch (err) {
      const message = apiError(err);
      // Pin a duplicate-number rejection to the field it belongs to.
      if (/mobile number/i.test(message)) setErrors({ phone: message });
      else setErrors({ agencyName: message });
    }
  };

  if (created) {
    return (
      <div className="content-narrow animate-up">
        <div className="card card-pad" style={{ textAlign: 'center', padding: 34 }}>
          <CheckCircle size={48} color="var(--ok)" weight="fill" />
          <h2 style={{ fontSize: 21, margin: '14px 0 7px' }}>{created.agency.name} is set up</h2>
          <p className="t-md muted" style={{ margin: '0 auto', maxWidth: 440, lineHeight: 1.65 }}>
            {created.message} They sign in with{' '}
            <strong style={{ color: 'var(--ink)' }}>{created.agency.phone}</strong>, and we text a
            6-digit code to it each time.
          </p>

          <div style={{ display: 'flex', gap: 9, justifyContent: 'center', marginTop: 24 }}>
            <Link className="btn btn-primary" to={`/agencies/${created.agency._id}`}>
              <ArrowSquareOut size={15} /> Open agency
            </Link>
            <button
              className="btn"
              onClick={() => {
                setForm(empty);
                setCreated(null);
              }}
            >
              Onboard another
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="content-narrow stack animate-up" style={{ gap: 18 }}>
      <div className="preview-note" style={{ borderStyle: 'solid', borderColor: 'var(--ac-tint-2)', background: 'var(--ac-tint)' }}>
        <Info size={17} color="var(--ac)" style={{ flex: 'none', marginTop: 1 }} />
        <span>
          <strong>You don&apos;t set a PIN.</strong> The agency signs in with the mobile number
          you type below: we text a 6-digit code to it each time. Nobody here ever holds a
          credential, and there is nothing for them to activate.
        </span>
      </div>

      <div className="card">
        <div className="card-head">
          <Buildings size={17} color="var(--ac)" />
          <h2 className="card-title">Agency</h2>
        </div>
        <div className="card-pad stack" style={{ gap: 14 }}>
          <Field label="Agency name" error={errors.agencyName} required>
            <input
              className={`input${errors.agencyName ? ' has-error' : ''}`}
              name="agencyName"
              value={form.agencyName}
              onChange={(e) => set('agencyName', e.target.value)}
              placeholder="e.g. Horizon Realty"
            />
          </Field>

          <div className="grid grid-2" style={{ gap: 14 }}>
            <Field label="Address">
              <input
                className="input"
                name="address"
                value={form.address}
                onChange={(e) => set('address', e.target.value)}
                placeholder="Office address"
              />
            </Field>
            <Field label="Website">
              <input
                className="input"
                name="website"
                value={form.website}
                onChange={(e) => set('website', e.target.value)}
                placeholder="optional"
              />
            </Field>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <User size={17} color="var(--ac)" />
          <h2 className="card-title">Who runs it</h2>
        </div>
        <div className="card-pad stack" style={{ gap: 14 }}>
          <Field label="Contact name" error={errors.contactName} required>
            <input
              className={`input${errors.contactName ? ' has-error' : ''}`}
              name="contactName"
              value={form.contactName}
              onChange={(e) => set('contactName', e.target.value)}
              placeholder="e.g. Deepak Kale"
            />
          </Field>

          <div className="grid grid-2" style={{ gap: 14 }}>
            <Field
              label="Mobile number"
              hint="What they sign in with."
              error={errors.phone}
              required
            >
              <input
                className={`input${errors.phone ? ' has-error' : ''}`}
                name="phone"
                inputMode="numeric"
                maxLength={10}
                value={form.phone}
                onChange={(e) => set('phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
                placeholder="10-digit number"
              />
            </Field>
            <Field
              label="Email"
              hint="Where we tell them the workspace is ready."
              error={errors.email}
              required
            >
              <input
                className={`input${errors.email ? ' has-error' : ''}`}
                name="email"
                type="email"
                value={form.email}
                onChange={(e) => set('email', e.target.value)}
                placeholder="owner@agency.com"
              />
            </Field>
          </div>
        </div>
      </div>

      <div className="row">
        <SubmitButton
          type="submit"
          pending={onboard.isPending}
          pendingLabel="Creating…"
          className="btn btn-primary"
        >
          Create agency &amp; send invite
        </SubmitButton>
        <Link className="btn" to="/agencies">
          Cancel
        </Link>
      </div>
    </form>
  );
}
